import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, Marker, DirectionsRenderer, OverlayView } from '@react-google-maps/api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Phone, MessageSquare, AlertTriangle, Star, X, Navigation, Siren, Share2, Layers, Locate, ShieldCheck, ChevronRight } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { CapacitorSafePoller, makePhoneCall, triggerHapticFeedback } from '../../utils/capacitorHelper';
import CreditReceipt from './CreditReceipt';
import InAppNavigationView from './InAppNavigationView';
import DriverProfileSheet from './DriverProfileSheet';
import CartoonCar from './CartoonCar';
import Vehicle3D, { topCarSrc, carColorHex } from './Vehicle3D';
import RideChat from './RideChat';
import useRideChatUnread from '../../hooks/useRideChatUnread';

const YACHANGU_LOGO =
  'https://customer-assets.emergentagent.com/job_yachangu-pay/artifacts/ynseryay_EE6C3FEA-5C5E-424A-B08C-ED1F2A53B790.jpeg';

const glassPanel = {
  backgroundColor: 'rgba(247, 249, 251, 0.82)',
  backdropFilter: 'blur(24px) saturate(180%)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
};

// 50%-transparent glass for the driver-accepted bottom sheet
const sheetGlass = {
  backgroundColor: 'rgba(247, 249, 251, 0.5)',
  backdropFilter: 'blur(28px) saturate(180%)',
  WebkitBackdropFilter: 'blur(28px) saturate(180%)',
};

// Resolve a possibly-relative upload path to an absolute backend URL
const resolveDriverPhoto = (driver) => {
  const url = driver?.profile_picture || driver?.face_photo_url;
  if (!url) return 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=200&q=80';
  return url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const mapContainerStyle = {
  width: '100%',
  height: '100vh'
};

// Bearing (0-360, 0 = north) between two lat/lng points
const computeBearing = (from, to) => {
  if (!from || !to) return null;
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

// Rough metres between two points (to ignore GPS jitter when computing heading)
const metresBetween = (a, b) => {
  if (!a || !b) return 0;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

// hex (#rrggbb) → rgba string with the given alpha
const hexToRgba = (hex, alpha) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// Top-down car marker that rotates to the driver's heading, with a red rear bar.
// Size scales with the map zoom so it stays road-proportioned when zoomed out.
const CarMarker = ({ position, color, heading, zoom }) => {
  const hex = carColorHex(color);
  // zoom 16 ≈ full size; shrink as the user zooms out, clamp to a usable range
  const factor = Math.max(0.45, Math.min(1.35, ((zoom || 15) - 9) / 6.5));
  const w = Math.round(30 * factor);
  const h = Math.round(54 * factor);
  const ring = Math.round(40 * factor);
  const ring2 = Math.round(22 * factor);
  return (
  <OverlayView position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
    <div style={{ transform: 'translate(-50%, -50%)', pointerEvents: 'none', position: 'relative' }}>
      {/* pulse rings under the car (do not rotate), tinted to the vehicle colour */}
      <div
        className="animate-ping"
        style={{
          position: 'absolute', left: '50%', top: '50%',
          width: ring, height: ring, marginLeft: -ring / 2, marginTop: -ring / 2,
          borderRadius: '9999px', background: hexToRgba(hex, 0.4),
        }}
      />
      <div
        style={{
          position: 'absolute', left: '50%', top: '50%',
          width: ring2, height: ring2, marginLeft: -ring2 / 2, marginTop: -ring2 / 2,
          borderRadius: '9999px', background: hexToRgba(hex, 0.3),
        }}
      />
      <div
        style={{
          width: w,
          height: h,
          transform: `rotate(${heading || 0}deg)`,
          transition: 'transform 0.6s ease-out, width 0.2s, height 0.2s',
          position: 'relative',
        }}
      >
        <img
          src={topCarSrc(color)}
          alt="driver vehicle"
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block',
                   filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.35))' }}
        />
        {/* rear indicator (bottom = back of the top-down car) */}
        <div style={{ position: 'absolute', left: '50%', bottom: 1, transform: 'translateX(-50%)',
                      width: Math.max(8, Math.round(16 * factor)), height: Math.max(2, Math.round(3 * factor)),
                      borderRadius: 2, background: '#ef4444' }} />
      </div>
    </div>
  </OverlayView>
  );
};


/**
 * Live Ride Tracking Component
 * - Shows driver profile, vehicle details, rating
 * - Real-time driver location updates (every 5 seconds)
 * - Cancel ride option with fee warning
 * - Opens payment modal on trip completion
 */
const LiveRideTracking = ({ 
  rideDetails, 
  driverDetails,
  onCancelRide,
  onTripCompleted
}) => {
  const [map, setMap] = useState(null);
  const [mapZoom, setMapZoom] = useState(15);
  const [driverLocation, setDriverLocation] = useState(null);
  const [driverHeading, setDriverHeading] = useState(0);
  const [driverBusy, setDriverBusy] = useState(false);
  const [driverCurrentDropoff, setDriverCurrentDropoff] = useState(null);
  const [busyLeg, setBusyLeg] = useState(null); // gray: driver -> other dropoff
  const [freeLeg, setFreeLeg] = useState(null); // green: other dropoff -> pickup
  const [busyEta, setBusyEta] = useState(null); // ETA for the gray (other-ride) leg
  const [busyLabelPos, setBusyLabelPos] = useState(null); // where to float the label
  const [showBusyPopup, setShowBusyPopup] = useState(false); // tap-to-view pickup estimate
  const [pickupTargetTs, setPickupTargetTs] = useState(null); // absolute ms target for pickup
  const [countdown, setCountdown] = useState(null); // live seconds remaining to pickup
  const prevDriverLocRef = useRef(null);
  const fitKeyRef = useRef(null);
  const [directions, setDirections] = useState(null);
  const [rideStatus, setRideStatus] = useState(rideDetails?.status || 'accepted');
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [distanceTraveled, setDistanceTraveled] = useState(0);
  const [pickupDistance, setPickupDistance] = useState(0);
  const [locationPoller, setLocationPoller] = useState(null);
  const [eta, setEta] = useState(null);
  const [panelHeight, setPanelHeight] = useState('normal'); // Start at 'normal'
  // SOS (family rides only)
  const [showSosConfirm, setShowSosConfirm] = useState(false);
  const [sosSending, setSosSending] = useState(false);
  const [sosSent, setSosSent] = useState(false);
  const [showInAppNav, setShowInAppNav] = useState(false);
  const [showDriverProfile, setShowDriverProfile] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const unreadChat = useRideChatUnread(rideDetails?.id, 'rider', showChat, toast);

  const [vehicleView, setVehicleView] = useState(() => {
    try {
      return localStorage.getItem('yachangu_vehicle_view') || '3d';
    } catch {
      return '3d';
    }
  });
  const toggleVehicleView = () => {
    setVehicleView((prev) => {
      const next = prev === '3d' ? '2d' : '3d';
      try {
        localStorage.setItem('yachangu_vehicle_view', next);
      } catch { /* ignore */ }
      return next;
    });
  };
  const isFamilyRide = !!rideDetails?.family_id;

  // Recenter map onto the driver's live location
  const recenterMap = () => {
    if (map && driverLocation) {
      map.panTo(driverLocation);
      map.setZoom(16);
    }
  };

  // Fit the map so the driver AND the next target are always in view, with
  // extra bottom padding so nothing hides behind the driver info sheet.
  const fitToRoute = () => {
    if (!map || !driverLocation || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(driverLocation);
    const goingToPickup = rideStatus === 'accepted' || rideStatus === 'on_the_way' || rideStatus === 'arrived';
    const target = goingToPickup
      ? { lat: rideDetails.pickup_location.latitude, lng: rideDetails.pickup_location.longitude }
      : { lat: rideDetails.dropoff_location.latitude, lng: rideDetails.dropoff_location.longitude };
    bounds.extend(target);
    if (driverCurrentDropoff) bounds.extend(driverCurrentDropoff);
    map.fitBounds(bounds, { top: 90, right: 60, bottom: 340, left: 60 });
  };

  // Auto-fit once whenever the leg/status changes (not on every location poll),
  // so the driver stays visible without fighting the rider's own pan/zoom.
  useEffect(() => {
    if (!map || !driverLocation) return;
    const key = `${rideStatus}|${!!driverCurrentDropoff}`;
    if (fitKeyRef.current === key) return;
    fitKeyRef.current = key;
    fitToRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, driverLocation, rideStatus, driverCurrentDropoff]);

  const handleSafetyKit = () => {
    triggerHapticFeedback('light');
    if (isFamilyRide) {
      setShowSosConfirm(true);
    } else {
      toast({
        title: 'Safety toolkit',
        description: 'In an emergency call 997 (Police) or 998 (Ambulance). Share your live trip with a trusted contact.',
      });
    }
  };

  const handleShareStatus = async () => {
    triggerHapticFeedback('light');
    const shareText = `I'm on a Yachangu ride with ${driverDetails?.name || 'my driver'} (${driverDetails?.vehicle?.plate_number || 'vehicle'}). ETA ${eta || 'soon'}.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My Yachangu trip', text: shareText });
      } else {
        await navigator.clipboard?.writeText(shareText);
        toast({ title: 'Trip details copied', description: 'Share them with a trusted contact.' });
      }
    } catch (_) {
      /* user dismissed share sheet */
    }
  };

  // Arrival clock string derived from the live ETA text
  const arrivalTime = (() => {
    if (!eta) return null;
    const mins = parseInt(eta, 10);
    if (Number.isNaN(mins)) return null;
    return new Date(Date.now() + mins * 60000).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  })();

  // Debug logging
  useEffect(() => {
    console.log('🔍 LiveRideTracking props:', {
      rideDetails: rideDetails,
      driverDetails: driverDetails,
      hasDriver: !!driverDetails,
      driverName: driverDetails?.name,
      driverVehicle: driverDetails?.vehicle
    });
  }, [rideDetails, driverDetails]);

  const isBike = rideDetails?.vehicle_type === 'bike';

  // Initialize driver location from props
  useEffect(() => {
    if (driverDetails?.current_location && !driverLocation) {
      setDriverLocation({
        lat: driverDetails.current_location.latitude,
        lng: driverDetails.current_location.longitude
      });
    }
  }, [driverDetails, driverLocation]);

  // Poll driver location every 5 seconds with Capacitor-safe polling
  useEffect(() => {
    if (!rideDetails?.id || rideStatus === 'completed') return;

    const pollDriverLocation = async () => {
      try {
        console.log(`🔄 Polling driver location for ride: ${rideDetails.id}`);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rides/${rideDetails.id}/driver-location`);
        const data = await response.json();
        
        console.log('📍 Driver location data:', data);
        
        if (data.location) {
          const newLocation = {
            lat: data.location.latitude,
            lng: data.location.longitude
          };
          // Compute heading from movement (backend sends no heading). Only
          // update when the driver actually moved enough to avoid GPS jitter.
          const prev = prevDriverLocRef.current;
          if (prev && metresBetween(prev, newLocation) > 4) {
            const b = computeBearing(prev, newLocation);
            if (b != null) setDriverHeading(b);
          }
          prevDriverLocRef.current = newLocation;
          console.log('✅ Setting driver location:', newLocation);
          setDriverLocation(newLocation);
        } else {
          console.warn('⚠️ No driver location in response');
        }

        // Busy-driver dual-route data
        setDriverBusy(!!data.driver_busy);
        if (data.driver_current_dropoff && data.driver_current_dropoff.latitude != null) {
          setDriverCurrentDropoff({
            lat: data.driver_current_dropoff.latitude,
            lng: data.driver_current_dropoff.longitude,
          });
        } else {
          setDriverCurrentDropoff(null);
        }

        // Update ride status
        if (data.status) {
          setRideStatus(data.status);
          
          // Trip completed
          if (data.status === 'completed') {
            if (poller) {
              poller.stop();
            }
            onTripCompleted(rideDetails);
          }
        }

        // Update distance traveled (for cancellation fee calculation)
        if (data.distance_traveled !== undefined) {
          setDistanceTraveled(data.distance_traveled);
        }
        if (data.pickup_distance !== undefined) {
          setPickupDistance(data.pickup_distance);
        }
      } catch (error) {
        console.error('❌ Error polling driver location:', error);
      }
    };

    // Call immediately on mount
    console.log('🚀 Starting driver location polling...');
    pollDriverLocation();

    // Create Capacitor-safe poller that pauses when app is backgrounded
    const poller = new CapacitorSafePoller(pollDriverLocation, 5000);
    poller.start();
    setLocationPoller(poller);

    return () => {
      if (poller) {
        poller.stop();
      }
    };
  }, [rideDetails?.id, rideStatus, onTripCompleted]);

  // Calculate route(s). Normal: driver -> pickup/dropoff (green). When the
  // driver is still finishing another trip: gray leg driver -> other dropoff,
  // then green leg other dropoff -> pickup.
  useEffect(() => {
    if (!driverLocation || !window.google) return;

    const directionsService = new window.google.maps.DirectionsService();
    const goingToPickup = rideStatus === 'accepted' || rideStatus === 'on_the_way';
    const pickup = { lat: rideDetails.pickup_location.latitude, lng: rideDetails.pickup_location.longitude };
    const dropoff = { lat: rideDetails.dropoff_location.latitude, lng: rideDetails.dropoff_location.longitude };

    const route = (origin, destination) => new Promise((resolve) => {
      directionsService.route(
        { origin, destination, travelMode: window.google.maps.TravelMode.DRIVING },
        (result, status) => resolve(status === 'OK' ? result : null)
      );
    });

    // Busy driver → two-leg view (only while still heading to pickup)
    if (driverBusy && driverCurrentDropoff && goingToPickup) {
      Promise.all([
        route(driverLocation, driverCurrentDropoff),
        route(driverCurrentDropoff, pickup),
      ]).then(([gray, green]) => {
        setBusyLeg(gray);
        setFreeLeg(green);
        setDirections(null);
        const grayLeg = gray?.routes?.[0]?.legs?.[0];
        const greenLeg = green?.routes?.[0]?.legs?.[0];
        const legs = [grayLeg, greenLeg].filter(Boolean);
        if (legs.length) {
          const totalSec = legs.reduce((s, l) => s + (l.duration?.value || 0), 0);
          setEta(`${Math.max(1, Math.round(totalSec / 60))} min`);
          setPickupTargetTs(Date.now() + totalSec * 1000);
        }
        // Separate ETA + floating position for the "finishing another ride" label
        if (grayLeg) {
          setBusyEta(`${Math.max(1, Math.round((grayLeg.duration?.value || 0) / 60))} min`);
          const path = gray?.routes?.[0]?.overview_path;
          const mid = path && path.length ? path[Math.floor(path.length / 2)] : null;
          setBusyLabelPos(
            mid
              ? { lat: mid.lat(), lng: mid.lng() }
              : {
                  lat: (driverLocation.lat + driverCurrentDropoff.lat) / 2,
                  lng: (driverLocation.lng + driverCurrentDropoff.lng) / 2,
                }
          );
        }
      });
      return;
    }

    // Normal single-leg route
    setBusyLeg(null);
    setFreeLeg(null);
    setBusyEta(null);
    setBusyLabelPos(null);
    setPickupTargetTs(null);
    route(driverLocation, goingToPickup ? pickup : dropoff).then((result) => {
      if (result) {
        setDirections(result);
        if (result.routes[0]?.legs[0]) setEta(result.routes[0].legs[0].duration.text);
      }
    });
  }, [driverLocation, rideStatus, rideDetails, driverBusy, driverCurrentDropoff]);

  // Live countdown to pickup while the busy-driver popup is open.
  useEffect(() => {
    if (!showBusyPopup || !pickupTargetTs) {
      setCountdown(null);
      return;
    }
    const tick = () => setCountdown(Math.max(0, Math.round((pickupTargetTs - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [showBusyPopup, pickupTargetTs]);

  const formatCountdown = (secs) => {
    if (secs == null) return null;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleCancelClick = () => {
    setShowCancelWarning(true);
  };

  const handleConfirmCancel = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rides/${rideDetails.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          cancelled_by: 'rider',
          reason: 'rider_cancelled' 
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Ride Cancelled',
          description: data.cancellation_fee > 0 
            ? `Cancellation fee: MWK ${data.cancellation_fee.toLocaleString()}` 
            : 'No cancellation fee applied'
        });
        onCancelRide(data);
      } else {
        toast({
          title: 'Error',
          description: data.message || 'Failed to cancel ride',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error cancelling ride:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel ride',
        variant: 'destructive'
      });
    }
    setShowCancelWarning(false);
  };

  // -------- SOS (family rides) --------
  const handleTriggerSos = async () => {
    if (!rideDetails?.family_id || !rideDetails?.rider_id) {
      toast({ title: 'SOS unavailable', description: 'This ride is not linked to a family profile.', variant: 'destructive' });
      setShowSosConfirm(false);
      return;
    }
    setSosSending(true);
    try { triggerHapticFeedback?.('heavy'); } catch (_) { /* no-op */ }

    // Best-effort geolocation (driver's live location as a fallback)
    const coords = await new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(driverLocation || null),
        { enableHighAccuracy: true, timeout: 4000 }
      );
    });

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/family/${rideDetails.family_id}/sos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_user_id: rideDetails.rider_id,
          ride_id: rideDetails.id,
          latitude: coords?.lat ?? null,
          longitude: coords?.lng ?? null,
          message: 'Emergency alert from active ride',
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSosSent(true);
      toast({ title: 'SOS sent', description: 'Your guardian has been notified with your location.' });
    } catch (err) {
      toast({ title: 'SOS failed', description: 'Please try again or call emergency services.', variant: 'destructive' });
    } finally {
      setSosSending(false);
      setShowSosConfirm(false);
    }
  };

  const getCancellationFeeMessage = () => {    const traveledPercentage = pickupDistance > 0 ? (distanceTraveled / pickupDistance) * 100 : 0;
    
    if (traveledPercentage > 50) {
      return {
        willCharge: true,
        message: `⚠️ Driver has traveled ${Math.round(traveledPercentage)}% of the way. Cancellation fee will apply.`
      };
    } else {
      return {
        willCharge: false,
        message: `✓ Driver is still far away (${Math.round(traveledPercentage)}% traveled). No cancellation fee.`
      };
    }
  };

  const getStatusDisplay = () => {
    switch (rideStatus) {
      case 'accepted':
        return { text: 'Driver Accepted', color: 'text-green-600', bg: 'bg-green-50' };
      case 'on_the_way':
        return { text: 'Driver On The Way', color: 'text-blue-600', bg: 'bg-blue-50' };
      case 'arrived':
        return { text: 'Driver Arrived', color: 'text-purple-600', bg: 'bg-purple-50' };
      case 'in_progress':
        return { text: 'Trip In Progress', color: 'text-orange-600', bg: 'bg-orange-50' };
      default:
        return { text: 'Active', color: 'text-gray-600', bg: 'bg-gray-50' };
    }
  };

  const statusDisplay = getStatusDisplay();
  const cancellationInfo = getCancellationFeeMessage();

  return (
    <div className="relative h-screen w-full bg-gray-100 dark:bg-gray-900">
      {/* Full-Screen Map */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        onLoad={(m) => {
          setMap(m);
          const initial = driverLocation || { lat: rideDetails.pickup_location.latitude, lng: rideDetails.pickup_location.longitude };
          m.setCenter(initial);
          m.setZoom(15);
          setMapZoom(15);
          m.addListener('zoom_changed', () => setMapZoom(m.getZoom()));
        }}
        options={{
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        }}
      >
        {/* Driver Location - rotating top-down car with red rear */}
        {driverLocation && (
          <CarMarker
            position={driverLocation}
            color={driverDetails?.vehicle?.color}
            heading={driverHeading}
            zoom={mapZoom}
          />
        )}

        {/* Other customer's drop-off dot (driver finishing another ride) */}
        {busyLeg && driverCurrentDropoff && (
          <Marker
            position={driverCurrentDropoff}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 6,
              fillColor: '#fb923c',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            }}
          />
        )}

        {/* Pickup Location Marker */}
        <Marker
          position={{ lat: rideDetails.pickup_location.latitude, lng: rideDetails.pickup_location.longitude }}
          icon={{
            url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png"
          }}
        />

        {/* Dropoff Location Marker */}
        <Marker
          position={{ lat: rideDetails.dropoff_location.latitude, lng: rideDetails.dropoff_location.longitude }}
          icon={{
            url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png"
          }}
        />

        {/* Route Line — normal single leg (green) */}
        {directions && !busyLeg && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: {
                strokeColor: '#059669',
                strokeWeight: 5,
                strokeOpacity: 0.9
              }
            }}
          />
        )}

        {/* Busy driver: gray leg (driver → other customer's dropoff) */}
        {busyLeg && (
          <DirectionsRenderer
            directions={busyLeg}
            options={{
              suppressMarkers: true,
              preserveViewport: true,
              polylineOptions: { strokeColor: '#fb923c', strokeWeight: 5, strokeOpacity: 0.9 }
            }}
          />
        )}
        {/* Busy driver: floating "finishing another ride · ~X min" label on the orange leg */}
        {busyLeg && busyLabelPos && (
          <OverlayView position={busyLabelPos} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
            <button
              type="button"
              data-testid="busy-eta-label"
              onClick={() => setShowBusyPopup(true)}
              style={{
                transform: 'translate(-50%, -50%)',
                whiteSpace: 'nowrap',
                background: '#fb923c',
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                padding: '3px 8px',
                borderRadius: 9999,
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                border: '1.5px solid #fff',
                cursor: 'pointer',
              }}
            >
              finishing another ride{busyEta ? ` · ~${busyEta}` : ''}
            </button>
          </OverlayView>
        )}
        {/* Busy driver: green free leg (other dropoff → your pickup) */}
        {freeLeg && (
          <DirectionsRenderer
            directions={freeLeg}
            options={{
              suppressMarkers: true,
              preserveViewport: true,
              polylineOptions: { strokeColor: '#059669', strokeWeight: 5, strokeOpacity: 0.95 }
            }}
          />
        )}
      </GoogleMap>

      {/* Glass trip-status overlay (top-left) */}
      <div className="fixed left-4 top-4 z-[52]" data-testid="ride-status-overlay">
        <div
          className="px-5 py-3.5 rounded-2xl shadow-xl border border-white/40 border-l-4 border-l-emerald-500"
          style={glassPanel}
        >
          <div className="flex items-center gap-2.5">
            <img src={YACHANGU_LOGO} alt="Yachangu" className="w-7 h-7 rounded-lg object-cover" />
            <div>
              <span className="block text-lg font-bold text-gray-900 leading-tight" data-testid="ride-status-eta">
                {eta ? `${eta} away` : statusDisplay.text}
              </span>
              <span className="block text-xs text-gray-500">
                {arrivalTime ? `Arriving at ${arrivalTime}` : 'Updating route…'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating map controls (right) */}
      <div className="fixed right-4 top-1/2 -translate-y-1/2 z-[52] flex flex-col gap-3">
        <button
          onClick={recenterMap}
          data-testid="ride-recenter-btn"
          aria-label="Recenter on driver"
          className="w-12 h-12 rounded-xl border border-white/40 shadow-lg flex items-center justify-center text-gray-700 active:scale-90 transition-transform"
          style={glassPanel}
        >
          <Locate className="w-5 h-5" />
        </button>
        <button
          onClick={() => {
            if (map) {
              const next = map.getMapTypeId() === 'roadmap' ? 'hybrid' : 'roadmap';
              map.setMapTypeId(next);
            }
          }}
          data-testid="ride-layers-btn"
          aria-label="Toggle map layers"
          className="w-12 h-12 rounded-xl border border-white/40 shadow-lg flex items-center justify-center text-gray-700 active:scale-90 transition-transform"
          style={glassPanel}
        >
          <Layers className="w-5 h-5" />
        </button>
      </div>


      {isFamilyRide && (
        <button
          type="button"
          onClick={() => setShowSosConfirm(true)}
          data-testid="ride-sos-btn"
          className={`fixed top-4 right-4 z-[55] flex items-center gap-1.5 px-3.5 py-2.5 rounded-full font-bold text-white shadow-xl transition-transform active:scale-95 ${
            sosSent
              ? 'bg-gray-500'
              : 'bg-red-600 hover:bg-red-700 ring-4 ring-red-300/40 animate-pulse'
          }`}
          aria-label="Trigger SOS alert"
        >
          <Siren className="w-4 h-4" />
          <span className="text-sm tracking-wide">{sosSent ? 'SOS sent' : 'SOS'}</span>
        </button>
      )}

      {/* Driver Info Panel - Bottom glass sheet */}
      <div
        className="fixed left-0 right-0 bottom-0 rounded-t-[32px] shadow-2xl border-t border-white/40 transition-all duration-300 ease-out"
        style={{
          ...sheetGlass,
          zIndex: 50,
          height: panelHeight === 'collapsed' ? '100px' : panelHeight === 'normal' ? 'auto' : '80vh',
          maxHeight: '80vh',
        }}
      >
        {/* Drag Handle */}
        <div
          className="flex justify-center py-3 cursor-pointer active:opacity-70"
          onClick={() => setPanelHeight(prev => (prev === 'normal' ? 'collapsed' : 'normal'))}
        >
          <div className="w-12 h-1.5 bg-gray-300/70 rounded-full"></div>
        </div>

        {panelHeight === 'collapsed' ? (
          /* Collapsed View - Mini */
          <div className="px-6 pb-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">{eta ? `${eta}` : 'Calculating…'}</h2>
              <div className="flex items-center gap-2">
                <img
                  src={resolveDriverPhoto(driverDetails)}
                  alt="Driver"
                  className="w-10 h-10 rounded-full object-cover"
                />
                <span className="text-sm font-medium text-gray-700">
                  {driverDetails?.vehicle?.plate_number || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        ) : (
          /* Normal & Expanded Views */
          <div className="px-5 sm:px-6 pb-6 overflow-y-auto" style={{ maxHeight: panelHeight === 'expanded' ? '70vh' : 'auto' }}>
            {/* Header: profile + name/rating + tinted cartoon car */}
            <div className="flex items-center gap-3 sm:gap-4 mb-3">
              <button
                onClick={() => {
                  triggerHapticFeedback('light');
                  setShowDriverProfile(true);
                }}
                data-testid="open-driver-profile"
                aria-label="View driver profile"
                className="relative flex-shrink-0 active:scale-95 transition-transform"
              >
                <img
                  src={resolveDriverPhoto(driverDetails)}
                  alt={driverDetails?.name}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover ring-4 ring-emerald-500/60"
                />
              </button>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1
                    className="text-2xl sm:text-3xl font-extrabold text-gray-900 truncate"
                    data-testid="tracking-driver-name"
                  >
                    {(driverDetails?.name || driverDetails?.first_name || 'Driver').split(' ')[0]}
                  </h1>
                  <span
                    className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-lg px-2 py-0.5 text-sm font-bold"
                    data-testid="tracking-driver-rating"
                  >
                    <Star className="w-3.5 h-3.5 fill-emerald-600 text-emerald-600" />
                    {driverDetails?.rating ?? 4.9}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  {{
                    accepted: 'Your driver is close by',
                    on_the_way: 'Your driver is close by',
                    arrived: 'Your driver has arrived',
                    in_progress: 'Enjoy your ride',
                  }[rideStatus] || 'Your driver is on the way'}
                </p>
              </div>

              <div className="w-24 sm:w-32 flex-shrink-0 flex flex-col items-center" data-testid="tracking-cartoon-car">
                {vehicleView === '3d' ? (
                  <Vehicle3D
                    color={driverDetails?.vehicle?.color}
                    title={`${driverDetails?.vehicle?.color || ''} ${driverDetails?.vehicle?.make || ''} ${driverDetails?.vehicle?.model || ''}`.trim()}
                    className="drop-shadow-lg"
                  />
                ) : (
                  <CartoonCar
                    color={driverDetails?.vehicle?.color}
                    title={`${driverDetails?.vehicle?.color || ''} ${driverDetails?.vehicle?.make || ''} ${driverDetails?.vehicle?.model || ''}`.trim()}
                  />
                )}
                <button
                  onClick={toggleVehicleView}
                  data-testid="vehicle-view-toggle"
                  className="mt-1 px-2 py-0.5 rounded-full bg-white/70 text-[10px] font-bold text-emerald-700 border border-emerald-200 active:scale-95 transition-transform"
                  aria-label="Toggle 2D / 3D vehicle view"
                >
                  {vehicleView === '3d' ? '3D' : '2D'}
                </button>
              </div>
            </div>

            {/* Vehicle description + plate + ETA */}
            <div className="flex items-end justify-between gap-3 mb-4">
              <div className="min-w-0">
                <p className="uppercase font-extrabold text-gray-800 leading-tight text-base sm:text-lg tracking-wide truncate">
                  {driverDetails?.vehicle?.color || ''} {driverDetails?.vehicle?.make || ''}
                </p>
                <p className="uppercase font-extrabold text-gray-800 leading-tight text-base sm:text-lg tracking-wide truncate">
                  {driverDetails?.vehicle?.model || 'Vehicle'}
                </p>
                <span
                  className="inline-block mt-1.5 bg-gray-900 text-white rounded-md px-3 py-1 text-base font-bold tracking-[0.18em]"
                  data-testid="tracking-plate"
                >
                  {driverDetails?.vehicle?.plate_number || 'N/A'}
                </span>
              </div>
              <p
                className="text-lg sm:text-xl font-extrabold text-emerald-700 text-right shrink-0 leading-tight"
                data-testid="tracking-eta"
              >
                {eta ? `Arriving in ${eta}` : 'Calculating…'}
              </p>
            </div>

            {/* Family/Personal credit applied — receipt row */}
            <CreditReceipt ride={rideDetails} />

            {/* Actions: Call / Message / Safety / Share */}
            <div className="grid grid-cols-4 gap-2 mt-1 mb-4">
              <button
                onClick={() => makePhoneCall(driverDetails?.phone)}
                data-testid="ride-call-btn"
                className="flex flex-col items-center gap-1.5 py-3 rounded-2xl bg-white/60 hover:bg-white active:scale-95 transition-all"
              >
                <span className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-emerald-700" />
                </span>
                <span className="text-xs font-semibold text-gray-700">Call</span>
              </button>
              <button
                onClick={() => {
                  triggerHapticFeedback('light');
                  setShowChat(true);
                }}
                data-testid="ride-message-btn"
                className="relative flex flex-col items-center gap-1.5 py-3 rounded-2xl bg-white/60 hover:bg-white active:scale-95 transition-all"
              >
                {unreadChat > 0 && (
                  <span
                    data-testid="ride-message-unread-badge"
                    className="absolute top-1.5 right-3 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow ring-2 ring-white animate-pulse"
                  >
                    {unreadChat > 9 ? '9+' : unreadChat}
                  </span>
                )}
                <span className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-emerald-700" />
                </span>
                <span className="text-xs font-semibold text-gray-700">Message</span>
              </button>
              <button
                onClick={handleSafetyKit}
                data-testid="ride-safety-btn"
                className="flex flex-col items-center gap-1.5 py-3 rounded-2xl bg-white/60 hover:bg-white active:scale-95 transition-all"
              >
                <span className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-700" />
                </span>
                <span className="text-xs font-semibold text-gray-700">Safety</span>
              </button>
              <button
                onClick={handleShareStatus}
                data-testid="ride-share-btn"
                className="flex flex-col items-center gap-1.5 py-3 rounded-2xl bg-white/60 hover:bg-white active:scale-95 transition-all"
              >
                <span className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-emerald-700" />
                </span>
                <span className="text-xs font-semibold text-gray-700">Share</span>
              </button>
            </div>

            {/* Navigation Button for Rider */}
            {rideStatus === 'in_progress' && (
              <button
                onClick={() => setShowInAppNav(true)}
                data-testid="rider-navigate-btn"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-full font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                <Navigation className="w-5 h-5" />
                <span>Navigate to Destination</span>
              </button>
            )}

            {/* View full profile */}
            <button
              onClick={() => {
                triggerHapticFeedback('light');
                setShowDriverProfile(true);
              }}
              data-testid="tracking-view-profile"
              className="w-full mt-3 flex items-center justify-center gap-1 text-sm font-semibold text-emerald-700 active:scale-95 transition-transform"
            >
              View full driver profile <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Full-screen Driver Profile overlay */}
      {showDriverProfile && (
        <DriverProfileSheet
          rideDetails={rideDetails}
          driverDetails={driverDetails}
          eta={eta}
          onClose={() => setShowDriverProfile(false)}
          onCancelRide={() => {
            setShowDriverProfile(false);
            handleCancelClick();
          }}
        />
      )}

      {/* In-app chat with the driver */}
      {showChat && (
        <RideChat
          rideId={rideDetails?.id}
          senderId={rideDetails?.rider_id}
          senderRole="rider"
          recipientName={driverDetails?.name || driverDetails?.first_name || 'Driver'}
          recipientPhone={driverDetails?.phone}
          onClose={() => setShowChat(false)}
        />
      )}

      {/* In-App Navigation Overlay */}
      {showInAppNav && (
        <InAppNavigationView
          directions={directions}
          dropoffName={rideDetails?.dropoff_location?.address || 'Destination'}
          onExit={() => setShowInAppNav(false)}
        />
      )}

      {/* Busy-driver: estimated pickup popup (tap the orange pill) */}
      {showBusyPopup && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          data-testid="busy-eta-popup"
          onClick={() => setShowBusyPopup(false)}
        >
          <Card
            className="max-w-sm w-full p-6 bg-white dark:bg-gray-800 border-2 border-orange-400"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <span className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Navigation className="w-5 h-5 text-orange-500" />
              </span>
              <div>
                <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1">Driver finishing another ride</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Your driver is dropping off another passenger first{busyEta ? ` (~${busyEta} away)` : ''}, then heading straight to you.
                </p>
              </div>
            </div>
            <div className="rounded-xl bg-orange-50 dark:bg-gray-700/50 p-4 text-center mb-4">
              <p className="text-xs uppercase tracking-wide font-semibold text-orange-600 dark:text-orange-300">Arriving in</p>
              <p className="text-4xl font-extrabold text-gray-900 dark:text-white mt-1 tabular-nums" data-testid="busy-eta-countdown">
                {countdown != null ? formatCountdown(countdown) : (eta || 'Calculating…')}
              </p>
              {arrivalTime && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-testid="busy-eta-pickup-time">
                  Estimated pickup at {arrivalTime}
                </p>
              )}
            </div>
            <Button
              onClick={() => setShowBusyPopup(false)}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="busy-eta-popup-close"
            >
              Got it
            </Button>
          </Card>
        </div>
      )}

      {/* SOS Confirmation Popup */}
      {showSosConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-70 p-4" data-testid="sos-confirm-modal">
          <Card className="max-w-sm w-full p-6 bg-white dark:bg-gray-800 border-2 border-red-500">
            <div className="flex items-start space-x-3 mb-4">
              <Siren className="w-8 h-8 text-red-600 flex-shrink-0 animate-pulse" />
              <div>
                <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1">Send SOS?</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Your guardian will be notified immediately with your current location and this ride's details.
                </p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                  For life-threatening emergencies, also call 997 / 991.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowSosConfirm(false)}
                variant="outline"
                className="flex-1 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                data-testid="sos-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleTriggerSos}
                disabled={sosSending}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
                data-testid="sos-confirm-btn"
              >
                {sosSending ? 'Sending…' : 'Send SOS'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Cancel Warning Popup */}
      {showCancelWarning && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <Card className="max-w-sm w-full p-6 bg-white dark:bg-gray-800">
            <div className="flex items-start space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-orange-500 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">Cancel Ride?</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {cancellationInfo.message}
                </p>
                {cancellationInfo.willCharge && (
                  <p className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                    A cancellation fee may apply.
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowCancelWarning(false)}
                variant="outline"
                className="flex-1 dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                Keep Ride
              </Button>
              <Button
                onClick={handleConfirmCancel}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LiveRideTracking;
